import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonButton, IonIcon, IonModal } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, pairwise } from 'rxjs';

import { AUTH_TELEMETRY, BIOMETRIC_SIGN_IN } from '@ninety/mobile/platform/auth';
import { appActions, PendingCredentials, SessionActions, SessionSelectors } from '@ninety/mobile/platform/state';

import { BIOMETRY_NAMES } from '../biometry-name/biometry-name';
import { ntyBottomSheetCss } from '../bottom-sheet/bottom-sheet.models';

import { enrollmentFailureMessage } from './enrollment-failure-message';

/**
 * What the offer says.
 *
 * Held here rather than inline in the template because the template is inside an `ion-modal`'s
 * `ng-template`, which jsdom never instantiates — so copy written inline is copy no test can read. These
 * strings are also the ones most likely to drift into saying something untrue, which is why they are
 * somewhere a test can assert on them.
 *
 * Two rules, and they pull in opposite directions.
 *
 * **Do not describe the mechanism.** No keychain, no "saved password", no "replay". Nobody choosing
 * whether to use Face ID needs to be told where a credential is written, and telling them reads as a
 * warning rather than a feature.
 *
 * **Do not claim security either.** The copy this replaces promised "quicker and more secure access",
 * and the second half was false — enrolling adds a convenience, not a protection. ADR 0003 has the
 * argument.
 *
 * What is left is what it actually does for the person: they stop typing their password. That is true,
 * it is the reason to say yes, and it describes nothing.
 */
export const BIOMETRIC_OFFER_COPY = {
  heading: (biometry: string) => `Sign in with ${biometry} next time?`,
  body: 'Skip typing your password when you come back to Ninety.',
  /**
   * Shown when "Don't ask me again" could not be recorded.
   *
   * It names the consequence rather than the cause, because the consequence is the only part anyone
   * can act on: the answer did not land, so the offer comes back. See {@link
   * BiometricOfferComponent.never}, which keeps the sheet up rather than closing on a write that
   * failed.
   */
  declineFailed: 'We could not save that. You may be asked again next time.',
} as const;

/**
 * The offer to store this sign-in for replay, made once per password sign-in and nowhere else.
 *
 * **The only place enrollment can happen**, and the only thing that puts a password on the device:
 * accepting is what writes it, see `SessionBiometricsEffects`. Which is why Settings is off-only.
 *
 * **What it asks before appearing is whether a password is in hand**, from `PendingCredentials`, rather
 * than whether the device has one stored — see that service for why. So an SSO-only account, which
 * supplies no password, can never enroll.
 *
 * **It is offered after a sign-in and nowhere else**, which costs a missed sheet its recovery — ADR 0003
 * § The trade accepts that.
 *
 * Mounted in the app shell rather than on a route, because it has to observe the machine reach
 * `authorized` and the `navigate` Command that follows is handled *after* the state advanced — a
 * component on the destination route mounts too late to see it. Deliberately not `main`'s home-page
 * modal keyed off a `checkBiometricSetup` query parameter, which put the trigger in a URL.
 */
@Component({
  selector: 'nty-biometric-offer',
  imports: [IonModal, IonIcon, IonButton],
  template: `
    <ion-modal
      id="biometric-offer"
      [cssClass]="modalCss"
      [isOpen]="open()"
      [canDismiss]="true"
      [initialBreakpoint]="1"
      [breakpoints]="[0, 1]"
      [handleBehavior]="'none'"
      (ionModalDidDismiss)="dismissed()">
      <ng-template>
        <div class="container">
          <div class="icon">
            <ion-icon class="biometric-icon" src="assets/icons/biometric--regular.svg" />
          </div>
          <div class="title terra-title">{{ heading() }}</div>
          <div class="sub-text nty-body">{{ body }}</div>
          @if (failure()) {
            <p class="terra-body-small failure">{{ failure() }}</p>
          }
          <div class="button-container">
            <ion-button class="terra-body enable" [disabled]="enrolling()" (click)="enable()">Enable</ion-button>
            <ion-button class="terra-body later" fill="outline" (click)="later()">Maybe Later</ion-button>
            <button type="button" class="do-not-ask terra-body" (click)="never()">Don't ask me again</button>
          </div>
        </div>
      </ng-template>
    </ion-modal>
  `,
  styles: `
    @use 'terra';

    ion-modal {
      --height: auto;
    }

    ion-button {
      --border-radius: 32px;
      margin-top: 8px;
      width: 100%;

      &::part(native) {
        height: 42px;
      }

      &:first-of-type {
        margin-top: 32px;
      }

      &[fill='outline'] {
        --border-color: #{terra.color(border)};
        --color: #{terra.color(text, light)};
      }
    }

    .container {
      background-color: terra.color(background);
      /*
       * A minimum rather than a fixed height, so the safe-area inset below makes the sheet taller
       * instead of squeezing what is in it. The contents are centred, so a fixed height would move the
       * last control up by half the inset and leave it under the navigation bar anyway.
       */
      min-height: 393px;
      padding: 24px;
      /*
       * The system navigation bar overlays the WebView: targetSdkVersion 36 means Android draws
       * edge-to-edge, and index.html asks for viewport-fit=cover. Without this, "Don't ask me again" —
       * the last thing in the sheet, and the only one with nothing beneath it — sits behind
       * three-button navigation, which is a taller inset than gesture navigation. Ionic defines the
       * variable from env(safe-area-inset-bottom); the fallback covers the browser, where there is none.
       */
      padding-bottom: calc(24px + var(--ion-safe-area-bottom, 0px));
      display: flex;
      justify-content: center;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .button-container {
      width: 100%;
    }

    .title {
      margin-bottom: 15px;
      font-size: 20px;
      color: terra.color(text);
    }

    .sub-text {
      color: terra.color(text, light);
    }

    /*
     * A real button, stripped back to the text it looks like.
     *
     * It reads as a link and has to stay that way, but it is the third answer to the question this
     * sheet asks and the only one that writes something permanently — so it cannot be a div that only
     * a pointer can reach. The reset is what a button adds over the div this replaced, and nothing
     * else: the terra-body class still supplies every font property.
     *
     * No backticks anywhere in here: this is inside an inline styles template literal, and one would
     * end it.
     */
    .do-not-ask {
      display: block;
      width: 100%;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      margin-top: 16px;
      margin-bottom: 8px;
      color: terra.color(text, light);
    }

    .failure {
      margin: 16px 0 0;
      color: terra.color(red);
    }

    .icon {
      font-size: 60px;
      line-height: 60px;

      ion-icon {
        margin-top: 10px;
      }
    }

    ion-icon.biometric-icon {
      color: terra.color(ninety);
    }

    .enable {
      --background: #{terra.color(ninety)};
      --background-activated: #{terra.color(ninety, light)};
      --color: #{terra.color(text-invert)};
    }

    .later {
      --background: #{terra.color(background)};
      --color: #{terra.color(text, light)};
    }
  `,
})
export class BiometricOfferComponent {
  private readonly store = inject(Store);
  private readonly biometrics = inject(BIOMETRIC_SIGN_IN);
  private readonly pending = inject(PendingCredentials);
  private readonly actions$ = inject(Actions);
  private readonly telemetry = inject(AUTH_TELEMETRY);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = signal(false);

  /**
   * Whether the enrollment currently settling is the one this sheet asked for.
   *
   * Only read to recognise a refusal, which is the one outcome that arrives without the task ever
   * running — see the subscription in the constructor. Not a signal: nothing renders it.
   */
  private requested = false;

  readonly enrolling = signal(false);
  readonly failure = signal<string | null>(null);
  readonly modalCss = [ntyBottomSheetCss];

  /**
   * What to call the authenticator in the heading.
   *
   * Resolved alongside the decision to offer at all rather than eagerly, because the sheet is the
   * only thing that reads it and both answers come from the same query. It cannot be `null` by the
   * time the sheet is visible — `canEnroll()` said yes, and it asks with `useFallback: false` — but
   * the type keeps that a consequence of the guard rather than an assumption printed into a heading.
   */
  readonly biometryLabel = signal<string | null>(null);

  /** The heading, naming the sensor this device would actually raise. */
  readonly heading = computed(() => BIOMETRIC_OFFER_COPY.heading(this.biometryLabel() ?? ''));

  readonly body = BIOMETRIC_OFFER_COPY.body;

  constructor() {
    /**
     * One way to arrive at a session is worth offering on, and it is a sign-in.
     *
     * `refreshing → authorized` is deliberately not the other: a restore has no password to enroll, and
     * every app resume runs one and lands on `authorized` by that same edge — so offering there would
     * mean asking again whenever the phone is put down.
     */
    this.store
      .select(SessionSelectors.state)
      .pipe(
        map(state => state.tag),
        pairwise(),
        filter(([before, after]) => before === 'authorizing' && after === 'authorized'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => void this.offerIfWorthwhile());

    /**
     * Leaving the foreground is not an answer, so it is recorded as the postponement it is.
     *
     * `PendingCredentials` discards the password on the same action independently of this. Closing the
     * sheet is this one's job: an unanswered question must not be sitting there when someone comes back
     * to an app whose password has since been forgotten, because tapping Enable would then report a
     * failure with nothing to explain it. That also makes Control Center cost someone the sheet — see
     * {@link appActions.appBackgrounded} — which is honest, since the password is gone either way.
     */
    this.actions$
      .pipe(ofType(appActions.appBackgrounded), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void (this.open() && this.later()));

    // The sheet stays up across the attempt, so this is the only place a failed enrollment can be
    // reported: nothing is stored and the machine is back at `idle`, carrying the cause.
    this.store
      .select(SessionSelectors.enrollment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ running, failure }) => {
        // Only the settling edge, so an unrelated state change — an app-resume refresh, say — cannot
        // close a sheet somebody is still reading.
        //
        // A refusal is the exception, and it needs one because it never runs: a request arriving during
        // a Session Restore is turned away with `sessionBusy` and no `running: true` before it, so there
        // is no edge to settle on and the tap would report nothing at all. `requested` is what keeps
        // this to *our* refusal — `taskRefused` is one field shared with the Company Switch, which can
        // be refused while this sheet happens to be open.
        const refused = this.requested && !running && failure?.kind === 'sessionBusy';
        const settled = this.enrolling() && !running;
        this.enrolling.set(running);
        if (!settled && !refused) return;
        this.requested = false;

        this.failure.set(enrollmentFailureMessage(failure));
        if (failure) return;

        this.telemetry.biometricOffer('enrolled');
        this.open.set(false);
      });
  }

  /**
   * Stays open until the attempt settles, because its outcome is the answer to what it asked.
   *
   * Needs no discard of its own: `SessionBiometricsEffects` takes the pair from `PendingCredentials`
   * when it handles the Command, and taking it is what clears it.
   */
  enable(): void {
    this.failure.set(null);
    this.requested = true;
    this.store.dispatch(
      SessionActions.eventReceived({ event: { type: 'biometricEnrollmentRequested', enabled: true } })
    );
  }

  /**
   * "Maybe later", which writes nothing to the device and forgets the password.
   *
   * Also where a swipe-dismiss and a background land — see {@link dismissed} and the constructor — and
   * the reason it records nothing more than a postponement: dismissing a sheet is not an answer to
   * "never ask again". Its own telemetry signal because a postponement and a refusal are different
   * facts — see {@link never}.
   *
   * **"Later" means the next password sign-in**, which is what the button says and what the discard
   * buys — the offer's condition is a password in hand, so letting go of this one is the postponement.
   * No suppression flag is needed.
   */
  later(): void {
    this.open.set(false);
    this.pending.discard();
    this.telemetry.biometricOffer('postponed');
  }

  /**
   * The sheet closed, from wherever.
   *
   * `ionModalDidDismiss` fires for **every** close, including the three this component performs itself
   * by setting {@link open} false — so routing it straight to {@link later} recorded a second answer on
   * top of the real one: a successful enrollment booked `enrolled` and then `postponed`, and "Don't ask
   * me again" booked `declined` and then `postponed` while setting a postponement nobody asked for.
   *
   * A swipe is the only dismissal that arrives with {@link open} still true, because nothing set it —
   * the signal is written synchronously at each of the three answers, and the modal's own event lands
   * after its animation. So the state of that signal is the whole test, and no extra flag is needed.
   */
  dismissed(): void {
    if (!this.open()) return;
    this.later();
  }

  /**
   * "Don't ask again", recorded permanently.
   *
   * Writes `main`'s `'do not ask'` into the enrollment preference rather than a key of its own, so the
   * answer survives an upgrade in both directions: someone who refused on `main` is not asked again here,
   * and nothing has to be migrated. A sign-out is what re-offers it, which is the same lifetime `main`
   * gives the answer.
   *
   * **Nothing is claimed before it is written.** The sheet used to close and report `declined` first and
   * await afterwards, from a template call that dropped the promise: a rejected write was an unhandled
   * rejection nobody saw, the person watched the sheet close as though it had been recorded, the
   * preference was never set, and the next launch asked again while the telemetry said they had refused.
   * So the order is inverted and the failure is caught — this is the one answer whose port call is not
   * routed through the machine.
   */
  async never(): Promise<void> {
    this.failure.set(null);
    try {
      await this.biometrics.decline();
    } catch {
      // The sheet stays up, because the answer did not land. Nothing is reported either: a refusal that
      // was not written is not a refusal, and booking one would put a decline in the rows for someone
      // this sheet is about to ask again.
      this.failure.set(BIOMETRIC_OFFER_COPY.declineFailed);
      return;
    }
    this.pending.discard();
    this.telemetry.biometricOffer('declined');
    this.open.set(false);
  }

  /**
   * Three reasons not to ask, checked in the order that costs least.
   *
   * **A password in hand is the one carrying the most weight.** It excludes every federated sign-in and
   * everyone who has already answered, because all three answers forget the password.
   *
   * `canEnroll()` is also what excludes the web build, where `WebBiometricSignIn` reports `false`, so no
   * platform check is needed here.
   */
  private async offerIfWorthwhile(): Promise<void> {
    if (!this.pending.has()) return;
    if (!(await this.biometrics.canEnroll())) return;
    // `enrolled` and `declined` are both answered; only `notEnrolled` is an open question.
    if ((await this.biometrics.enrollment()) !== 'notEnrolled') return;

    // Named before it is shown, so the heading never renders with a blank where the sensor goes.
    const label = BIOMETRY_NAMES[await this.biometrics.biometry()];
    if (label === null) return;

    this.biometryLabel.set(label);
    this.open.set(true);
    this.telemetry.biometricOffer('offered');
  }
}
